"use client";

import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { useClientEnvironment } from "~/app/environment/client";

/**
 * This is a client component that initializes the Datadog RUM client.
 * Using a component instead of a hook because this is used in layout.tsx and putting it in a child component wouldn't allow us to catch errors in all of the componets within layout
 * This component doesn't render anything
 */
export function DatadogRumInit() {
  const env = useClientEnvironment();

  // We only want to initialize Datadog RUM in beta and prod environments only

  useEffect(() => {
    if (!["beta", "prod"].includes(env.PS_ENV)) {
      return;
    }

    // Only initialize if we have the required credentials
    if (!env.DATADOG_RUM_APPLICATION_ID || !env.DATADOG_RUM_CLIENT_TOKEN) {
      return;
    }

    // 3rd-party domains loaded by Segment integrations that routinely fail
    // behind ad blockers / iOS content blockers / ITP, producing unactionable
    // RUM noise, CORS errors. Add new domains here when similar noise shows up.
    const blockedStackPatterns = [
      "ct.pinterest.com",
      "s.pinimg.com",
      "chrome-extension://",
      "www.googletagmanager.com",
    ];

    datadogRum.init({
      applicationId: env.DATADOG_RUM_APPLICATION_ID,
      clientToken: env.DATADOG_RUM_CLIENT_TOKEN,
      site: "us5.datadoghq.com",
      service: "web-listings-v2",
      version: env.RELEASE_TAG,
      env: env.PS_ENV,
      allowedTracingUrls: [
        {
          match: /^https?:\/\/.*\.peerspace\.com.*/, // Track internal API calls to peerspace.com
          propagatorTypes: ["tracecontext", "datadog"], // W3C traceparent + Datadog headers
        },
        {
          match: /^https?:\/\/.*\.peerspaceapp\.com.*/, // Track internal API calls to peerspaceapp.com
          propagatorTypes: ["tracecontext", "datadog"], // W3C traceparent + Datadog headers
        },
      ],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: "mask-user-input",
      trackBfcacheViews: true,
      trackUserInteractions: true,
      enablePrivacyForActionName: true,
      silentMultipleInit: true,
      propagateTraceBaggage: true,
      beforeSend: (event) => {
        if (event.type === "error") {
          const message = event.error?.message ?? "";
          const stack = event.error?.stack ?? "";
          const isLoadFailure =
            message.startsWith("Failed to load ") ||
            message === "Failed to fetch";
          if (
            isLoadFailure &&
            blockedStackPatterns.some((domain) => stack.includes(domain))
          ) {
            return false;
          }
        }
        return true;
      },
    });
  }, [
    env.DATADOG_RUM_APPLICATION_ID,
    env.DATADOG_RUM_CLIENT_TOKEN,
    env.PS_ENV,
    env.RELEASE_TAG,
  ]);

  return null;
}
